import { capturePostHogEvent } from '@/analytics/posthog';
import { getPageAnalyticsProps } from '@/lib/pageAnalyticsProps';

/**
 * Where on the page the CTA sits. Pages routinely render two or three CTAs
 * whose button text is identical, so autocapture alone can't tell them apart -
 * this is the property that makes "which slot actually works" answerable.
 */
export type CtaPosition =
  | 'hero'
  | 'inline'
  | 'footer'
  | 'calculator'
  // /compare/<pair> placements, top to bottom. Every link off that template to
  // another page has its own slot, so the funnel can tell the bridge to the
  // Delphina comparison pages apart from the closing ask.
  | 'breadcrumb'
  | 'verdict_bridge'
  | 'matrix_header'
  | 'coaching_section'
  | 'coaching_guide'
  | 'neither_right'
  | 'gap_reading'
  | 'gap_cta'
  | 'own_verdict'
  | 'next_question'
  | 'closing'
  | 'compare_individually'
  | 'other_comparisons'
  | 'related_guides'
  // Provider and broker pair pages (/pension-providers/comparison/<pair>,
  // /brokers/comparison/<pair>). Their existing CTAs keep hero / calculator /
  // inline / footer so their history stays continuous; these are the links
  // that were not tracked before.
  | 'table_header'
  | 'review_link';

/**
 * The `cta_clicked` attribution a shared component needs from its page. Passed
 * as one optional bundle so a component used on several templates only emits
 * `cta_clicked` where the page asks it to; `template`, `page_path`, `cta_text`
 * and `destination` are filled in at click time.
 */
export interface CtaTracking {
  position: CtaPosition;
  location?: string;
  pagePair?: string;
  variant?: string;
}

export interface CtaClickProps {
  /** Normalised page template, e.g. `/brokers/comparison/:pair`. */
  template: string;
  /** The concrete path the visitor was on. */
  page_path: string;
  cta_position: CtaPosition;
  cta_text: string;
  /** Where the CTA sends them. */
  destination: string;
  /** Optional label for distinguishing copy variants of the same slot. */
  variant?: string;
  /** Page family, e.g. `provider_comparison`. Omitted on other CTAs. */
  location?: string;
  /** Concrete comparison slug, e.g. `nest-vs-standard-life`. */
  page_pair?: string;
}

/** Destinations that count as a booking ask rather than a signup ask. */
const BOOKING_DESTINATION = /^\/book(\?|$|\/)/;

/**
 * Route patterns for the programmatic templates, most specific first.
 *
 * These deliberately mirror the project's PostHog path cleaning rules, down to
 * the `<angle-bracket>` naming: an insight can group by `template` on a
 * `cta_clicked` event and by the cleaned `$pathname` on a `$pageview`, and the
 * two produce identical strings, so they line up in one funnel. Changing a
 * pattern here means changing the matching path cleaning rule too.
 */
const TEMPLATE_PATTERNS: Array<[RegExp, string]> = [
  [/^\/can-i-retire-at\/[^/]+\/with\/[^/]+\/?$/, '/can-i-retire-at/<age>/with/<amount>'],
  [/^\/brokers\/comparison\/[^/]+\/?$/, '/brokers/comparison/<pair>'],
  [/^\/pension-providers\/comparison\/[^/]+\/?$/, '/pension-providers/comparison/<pair>'],
  [/^\/compare\/[^/]+\/?$/, '/compare/<pair>'],
  [/^\/locations\/[^/]+\/[^/]+\/?$/, '/locations/<location>/<pageType>'],
  [/^\/comparison\/[^/]+\/[^/]+\/?$/, '/comparison/<competitor>/<section>'],
  [/^\/comparison\/[^/]+\/?$/, '/comparison/<competitor>'],
  [/^\/brokers\/[^/]+\/?$/, '/brokers/<slug>'],
  [/^\/pension-providers\/[^/]+\/?$/, '/pension-providers/<slug>'],
  [/^\/locations\/[^/]+\/?$/, '/locations/<location>'],
  [/^\/blog\/tag\/[^/]+\/?$/, '/blog/tag/<tag>'],
  [/^\/blog\/[^/]+\/?$/, '/blog/<slug>'],
  [/^\/guides\/[^/]+\/?$/, '/guides/<slug>'],
  [/^\/videos\/[^/]+\/?$/, '/videos/<slug>'],
];

/**
 * Collapse a concrete path to its template. Paths that aren't programmatic
 * (`/pricing`, `/`) are already their own template and pass through unchanged.
 */
export function resolvePageTemplate(pathname: string): string {
  for (const [pattern, template] of TEMPLATE_PATTERNS) {
    if (pattern.test(pathname)) return template;
  }
  return pathname;
}

/**
 * Record a click on a marketing CTA.
 *
 * Clicks to `/book` also emit `booking_cta_clicked`. `cta_clicked` fires on
 * every CTA on the site, so it's too broad to use as a conversion goal -
 * this second event is narrow enough to point a PostHog conversion at
 * directly, and still carries `template` so it breaks down per page type.
 *
 * Consent and the SSR guard are handled by `capturePostHogEvent`, so this is
 * safe to call from any client component without checking either.
 */
export function trackCtaClick(props: CtaClickProps): void {
  // Page-level properties (e.g. `audience` on /compare/<pair>) come first, so
  // anything the CTA sets explicitly wins.
  const event = {
    ...getPageAnalyticsProps(),
    ...Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined)),
  };
  capturePostHogEvent('cta_clicked', event);

  if (BOOKING_DESTINATION.test(props.destination)) {
    capturePostHogEvent('booking_cta_clicked', event);
  }
}
